/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";
import { IInspectorControlProps } from "./InspectorTypes";
import {
	ISchemaConstArrayModel,
	ISchemaConstStringModel,
	TGenericBlueprintSchema,
	TGenericModelNode
} from "@adapptio/lib-blueprint";
import {
	InspectPanel,
	InspectPanelContent,
	InspectPanelHeader,
	InspectPanelHeaderInfo,
	InspectPanelHeaderInfoTitle
} from "../InspectPanel";
import { useTranslate } from "@adapptio/lib-components";
import { termsEditorInspector } from "../../../terms/inspector";
import { ToolbarButton } from "../Toolbar/ToolbarButton";
import { ToolbarCommandButton } from "../Toolbar/ToolbarCommandButton";
import { Inspector } from "../../../inspector";
import { InspectorTitle } from "./InspectorTitle";
import { useBlueprintModel, useCommand } from "../../hooks";

export interface IInspectArrayItemProps extends IInspectorControlProps {
	model: TGenericModelNode;
	parentListModel: ISchemaConstArrayModel<TGenericBlueprintSchema>;
	nameModel?: ISchemaConstStringModel;
	contentModel?: TGenericModelNode;
	title?: string;
}

export const InspectArrayItem: React.FC<IInspectArrayItemProps> = ({
	title,
	model,
	parentListModel,
	nameModel,
	contentModel
}) => {
	const t = useTranslate();

	useBlueprintModel(model);
	useBlueprintModel(parentListModel);

	const handleDelete = useCallback(() => {
		const index = parentListModel.items.findIndex((item) => item.nodeId === model.nodeId);

		if (index > -1) {
			parentListModel.schema.removeElement(parentListModel, index, true);
		}
	}, [ model, parentListModel ]);

	const [ duplicateArrayItem ] = useCommand("duplicate.arrayItem");

	const handleDuplicate = useCallback(() => {
		duplicateArrayItem(model, parentListModel);
	}, [ model, parentListModel ]);

	return (
		<InspectPanel>
			<InspectPanelHeader>
				<InspectPanelHeaderInfo>
					<InspectPanelHeaderInfoTitle>{title}</InspectPanelHeaderInfoTitle>

					<ToolbarButton
						icon="mdi/content-copy"
						tooltip={t("editor", termsEditorInspector.component.duplicate)}
						onClick={handleDuplicate}
					/>
					<ToolbarButton
						icon="mdi/delete"
						tooltip={t("editor", termsEditorInspector.component.delete)}
						onClick={handleDelete}
					/>
				</InspectPanelHeaderInfo>

				{nameModel && <InspectorTitle label="name" model={nameModel} />}
			</InspectPanelHeader>
			<InspectPanelContent>
				<Inspector model={contentModel || model} />
			</InspectPanelContent>
		</InspectPanel>
	);
};

InspectArrayItem.defaultProps = {
	title: ""
};
